import type { ProductName as ProductNameSchema } from '../../../../amplienceTypes/schemas/imported/product-name-schema';
import type { StyleVariant } from '../../../../utilities/graphql/codegen';
import type { ReactElement } from 'react';
import type { RuleSet } from 'styled-components';

import { setCookie } from 'cookies-next';
import { useEffect } from 'react';
import styled, { css } from 'styled-components';

import { VTA } from '../../../../utilities/constants/cookies';
import { PDP_VIEWS } from '../../../../utilities/constants/localStorageKeys';
import { useUserContext } from '../../../../utilities/context/dynamic/UserContext';
import { usePDPContext } from '../../../../utilities/context/static/PDPContext';
import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { CustomerType } from '../../../../utilities/graphql/codegen';
import { storageAvailable } from '../../../../utilities/helpers';
import { media } from '../../../shared/core/styles';
import { Typography } from '../../../shared/core/typography/Typography';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import { SiteType } from '~/utilities/graphql/codegen';

export interface ProductNameHybrisProps {
  productStyleVariantInfo?: StyleVariant | null;
}

const GStarProductNameStyles = css<{ $lowestPriceDisplay: string }>`
  font-weight: ${({ theme }) => theme.variables.font.weight.medium};
  font-size: ${({ theme }) => theme.font.size[4]};
  color: ${({ theme }) => theme.colors.content.primary};
  text-transform: capitalize;
  line-height: 1.4;

  @media ${({ theme }) => media(theme).greaterThan('sm')} {
    padding: 0 16px;
    line-height: 1.4;
  }

  @media ${({ theme }) => media(theme).greaterThan('lg')} {
    bottom: 0;
    padding: 0;
    font-size: ${({ theme }) => theme.font.size[6]};
    line-height: 1.4;
    text-align: left;
  }

  @media ${({ theme }) => media(theme).between('xs', 'lg')} {
    padding: 0;
  }
`;

const AafProductNameStyles = css<{ $lowestPriceDisplay: string }>`
  font-size: 14px;
  letter-spacing: 0.7px;
  text-align: left;
  padding: 0;
  line-height: 1;
  color: ${({ theme }) => theme.colors.content.primary};
`;
const GStarProductNameWrapperStyles = css<{ $lowestPriceDisplay: string }>`
  margin-top: 0;
  margin-bottom: 0;

  @media ${({ theme }) => media(theme).greaterThan('lg')} {
    margin-bottom: 0;
  }
`;
const AafProductNameWrapperStyles = css<{ $lowestPriceDisplay: string }>`
  margin: 12px 0 ${({ theme }) => theme.spacing.y.xs};

  @media ${({ theme }) => media(theme).greaterThan('lg')} {
    margin-bottom: 10px;
    min-height: 20px;
  }
`;

const ProductNameConfig: Record<SiteType, RuleSet<{ $lowestPriceDisplay: string }>> = {
  [SiteType.Gstar]: GStarProductNameStyles,
  [SiteType.Wahts]: GStarProductNameStyles,
  [SiteType.Outlet]: GStarProductNameStyles,
  [SiteType.Employeeshop]: GStarProductNameStyles,
  [SiteType.Aaf]: AafProductNameStyles,
};
const ProductNameWrapperConfig: Record<SiteType, RuleSet<{ $lowestPriceDisplay: string }>> = {
  [SiteType.Gstar]: GStarProductNameWrapperStyles,
  [SiteType.Wahts]: GStarProductNameWrapperStyles,
  [SiteType.Outlet]: GStarProductNameWrapperStyles,
  [SiteType.Employeeshop]: GStarProductNameWrapperStyles,
  [SiteType.Aaf]: AafProductNameWrapperStyles,
};

const S = {
  ProductNameWrapper: styled.div<{
    $lowestPriceDisplay: string;
  }>`
    position: relative;
    display: block;
    height: auto;

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      margin-top: 0;
    }
    ${({ theme }) => ProductNameWrapperConfig[theme.siteType]}
  `,
  ProductName: styled(Typography)<{ $lowestPriceDisplay: string }>`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 30px;

    ${({ theme }) => ProductNameConfig[theme.siteType]}
  `,
};

export const ProductName = ({
  htmlTag,
  productStyleVariantInfo,
}: ProductNameHybrisProps & ProductNameSchema): ReactElement => {
  const {
    product: { name, subTargetAudience },
  } = usePDPContext();
  const { hasAnalyticsConsent } = useConsentContext();
  const { user } = useUserContext();

  const {
    configuration: { lowestPriceDisplay },
  } = useStaticContext();

  useEffect(() => {
    if (
      hasAnalyticsConsent &&
      user !== undefined &&
      user?.customerType !== CustomerType.Registered &&
      subTargetAudience
    ) {
      const initialViews = {
        boys: 0,
        girls: 0,
        men: 0,
        women: 0,
      };

      if (storageAvailable('localStorage')) {
        const localPDPViews = localStorage.getItem(PDP_VIEWS);
        let views;

        try {
          views = localPDPViews ? JSON.parse(localPDPViews) : initialViews;
        } catch {
          views = initialViews;
        }

        if (subTargetAudience in views) {
          const viewsKey = subTargetAudience as keyof typeof views;
          const viewedNumber = views[viewsKey] + 1;

          if (viewedNumber >= 3) {
            setCookie(VTA, subTargetAudience, { maxAge: 60 * 60 * 24 * 365 });
            localStorage.setItem(PDP_VIEWS, JSON.stringify(initialViews));
          } else {
            views[viewsKey] = viewedNumber;
            localStorage.setItem(PDP_VIEWS, JSON.stringify(views));
          }
        }
      }
    }
  }, [user, subTargetAudience, hasAnalyticsConsent]);

  return (
    <S.ProductNameWrapper
      data-testid="summary-product-name"
      $lowestPriceDisplay={lowestPriceDisplay || 'NEVER'}
    >
      <S.ProductName
        $lowestPriceDisplay={lowestPriceDisplay || 'NEVER'}
        component={htmlTag}
        variant="h1"
        data-cs-capture=""
      >
        {productStyleVariantInfo?.name || name}
      </S.ProductName>
    </S.ProductNameWrapper>
  );
};
